// ═══════════════════════════════════════════
// AGENT PAGES
// ═══════════════════════════════════════════
import React, { useState, useEffect } from "react";
import { fmt } from "../../utils/fmt";
import api from "../../utils/api";
import {
  StatCard,
  DataTable,
  PageHeader,
  Button,
  Modal,
  FormInput,
  FormSelect,
  FormTextarea,
  Toggle,
  StatusBadge,
  PrintButton,
  PrintHeader,
} from "../../components/common";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
export function AgentDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/agent/dashboard").then((r) => setStats(r.data.data));
  }, []);

  // All AED/USDT figures are computed server-side from the rate frozen on each
  // transaction and settlement, so they never move when a rate is changed.
  const aedTodayRate = stats?.aedTodayRate || 0;
  const settledInr = parseFloat(stats?.confirmedSettlementInr || 0);
  const settledAed = parseFloat(stats?.confirmedSettlementAed || 0);
  const settledUsdt = parseFloat(stats?.confirmedSettlementUsdt || 0);

  return (
    <div>
      <PageHeader
        title="Agent Dashboard"
        subtitle={
          aedTodayRate
            ? `Today's AED rate ${aedTodayRate.toFixed(2)} · past entries stay at their own rate`
            : undefined
        }
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          title="Total Pay Out Amount"
          value={stats?.totalPayOutAmount || 0}
          index={2}
        />

        {/* Admin Commission To Pay */}
        <div className="bg-gradient-to-br from-orange-400 to-red-500 rounded-2xl p-5 text-white shadow-sm">
          <p className="text-sm font-medium opacity-80 mb-2">
            Admin Commission (To Pay)
          </p>
          <p className="text-xl font-bold">
            ₹{fmt(stats?.totalAdminCommission || 0)}
          </p>
          <div className="mt-2 pt-2 border-t border-white/20 text-xs">
            <div className="flex justify-between opacity-90">
              <span>In AED</span>
              <strong>AED {fmt(stats?.totalAdminCommissionAed || 0)}</strong>
            </div>
          </div>
        </div>

        <StatCard
          title="Total Agent Commission"
          value={stats?.totalAgentCommission || 0}
          index={0}
        />

        {/* Total Settlement Received */}
        <div className="bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl p-5 text-white shadow-sm">
          <p className="text-sm font-medium opacity-80 mb-2">
            Total Settlement Received
          </p>
          <p className="text-xl font-bold">₹{fmt(settledInr)}</p>
          <div className="mt-2 pt-2 border-t border-white/20 text-xs space-y-1">
            <div className="flex justify-between font-bold">
              <span>Total in AED</span>
              <span>AED {fmt(settledAed)}</span>
            </div>
            {settledUsdt > 0 && (
              <div className="flex justify-between font-bold">
                <span>Total in USDT</span>
                <span>USDT {fmt(settledUsdt)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Total Payment Lunga — negative means extra was settled, agent owes it back */}
        <div
          className={`bg-gradient-to-br rounded-2xl p-5 text-white shadow-sm ${
            (stats?.totalPaymentLunga || 0) < 0
              ? "from-rose-500 to-red-600"
              : "from-indigo-400 to-purple-500"
          }`}
        >
          <p className="text-sm font-medium opacity-80 mb-2">
            {(stats?.totalPaymentLunga || 0) < 0
              ? "Extra Settled (Dena)"
              : "Total Payment Lunga"}
          </p>
          <p className="text-xl font-bold">
            ₹{fmt(stats?.totalPaymentLunga || 0)}
          </p>
          <div className="mt-2 pt-2 border-t border-white/20 text-xs space-y-1">
            <div className="flex justify-between opacity-90">
              <span>In AED</span>
              <strong>AED {fmt(stats?.payOutInAed || 0)}</strong>
            </div>
            <div className="flex justify-between opacity-90">
              <span>In USDT</span>
              <strong>USDT {fmt(stats?.payOutInUsdt || 0)}</strong>
            </div>
          </div>
        </div>

        <StatCard
          title="Pending Amount"
          value={stats?.totalPendingAmount || 0}
          index={4}
        />
        <StatCard
          title="Available Details"
          value={stats?.availableLimit || 0}
          index={5}
        />
        <StatCard
          title="Total Pay Out Transactions"
          value={stats?.totalPayOutTransactions || 0}
          prefix=""
          index={1}
        />
        <StatCard
          title="Total Pending Transactions"
          value={stats?.totalPendingTransactions || 0}
          prefix=""
          index={3}
        />
      </div>
    </div>
  );
}

export function AgentOperators() {
  const [operators, setOperators] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null); // ✅ edit state
  const [form, setForm] = useState({
    name: "",
    maxTransactionAmount: "",
    minTransactionAmount: "",
    commissionChargePercent: "",
    description: "",
  });
  const [editForm, setEditForm] = useState({
    name: "",
    maxTransactionAmount: "",
    minTransactionAmount: "",
    commissionChargePercent: "",
    description: "",
    isActive: true,
  });

  const fetch = () =>
    api.get("/agent/operators").then((r) => setOperators(r.data.data));
  useEffect(() => {
    fetch();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/agent/operators", form);
      toast.success("Operator created!");
      setShowCreate(false);
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.message || "Error.");
    }
  };

  // ✅ open edit modal with existing data
  const handleEditOpen = (operator) => {
    setEditForm({
      name: operator.name,
      maxTransactionAmount: operator.maxTransactionAmount,
      minTransactionAmount: operator.minTransactionAmount,
      commissionChargePercent: operator.commissionChargePercent,
      description: operator.description || "",
      isActive: operator.isActive,
    });
    setShowEdit(operator);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/agent/operators/${showEdit.id}`, editForm);
      toast.success("Operator updated!");
      setShowEdit(null);
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.message || "Error.");
    }
  };

  return (
    <div>
      <PageHeader
        title="Operators List"
        action={<Button onClick={() => setShowCreate(true)}>Create</Button>}
      />
      <DataTable
        columns={[
          { header: "ID", key: "id" },
          { header: "Name", key: "name" },
          {
            header: "Max Txn",
            render: (r) =>
              `₹${parseFloat(r.maxTransactionAmount).toLocaleString()}`,
          },
          {
            header: "Min Txn",
            render: (r) =>
              `₹${parseFloat(r.minTransactionAmount).toLocaleString()}`,
          },
          { header: "Commission %", key: "commissionChargePercent" },
          {
            header: "Is Active",
            render: (r) => (
              <StatusBadge status={r.isActive ? "Active" : "Inactive"} />
            ),
          },
        ]}
        data={operators}
        total={operators.length}
        page={1}
        actions={(r) => (
          <div className="flex gap-1">
            <button
              onClick={() => handleEditOpen(r)}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-mac"
            >
              ✏️
            </button>
            <button
              onClick={async () => {
                await api.delete(`/agent/operators/${r.id}`);
                fetch();
              }}
              className="p-1.5 rounded-lg hover:bg-red-50 transition-mac"
            >
              🗑️
            </button>
          </div>
        )}
      />

      {/* Create Modal */}
      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="Operator Form"
      >
        <form onSubmit={handleCreate}>
          <FormInput
            label="Name"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Operator Name"
          />
          <FormInput
            label="Max Transaction Amount"
            required
            type="number"
            value={form.maxTransactionAmount}
            onChange={(e) =>
              setForm({ ...form, maxTransactionAmount: e.target.value })
            }
          />
          <FormInput
            label="Min Transaction Amount"
            required
            type="number"
            value={form.minTransactionAmount}
            onChange={(e) =>
              setForm({ ...form, minTransactionAmount: e.target.value })
            }
          />
          <FormInput
            label="Commission Charge Percent"
            required
            type="number"
            step="0.01"
            value={form.commissionChargePercent}
            onChange={(e) =>
              setForm({ ...form, commissionChargePercent: e.target.value })
            }
            placeholder="e.g 4, 0.8, 2.6"
          />
          <FormTextarea
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <Button type="submit" className="w-full">
            Save Operator
          </Button>
        </form>
      </Modal>

      {/* ✅ Edit Modal */}
      <Modal
        open={!!showEdit}
        onClose={() => setShowEdit(null)}
        title="Edit Operator"
      >
        <form onSubmit={handleEdit}>
          <FormInput
            label="Name"
            required
            value={editForm.name}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
          />
          <FormInput
            label="Max Transaction Amount"
            required
            type="number"
            value={editForm.maxTransactionAmount}
            onChange={(e) =>
              setEditForm({ ...editForm, maxTransactionAmount: e.target.value })
            }
          />
          <FormInput
            label="Min Transaction Amount"
            required
            type="number"
            value={editForm.minTransactionAmount}
            onChange={(e) =>
              setEditForm({ ...editForm, minTransactionAmount: e.target.value })
            }
          />
          <FormInput
            label="Commission Charge Percent"
            required
            type="number"
            step="0.01"
            value={editForm.commissionChargePercent}
            onChange={(e) =>
              setEditForm({
                ...editForm,
                commissionChargePercent: e.target.value,
              })
            }
          />
          <FormTextarea
            label="Description"
            value={editForm.description}
            onChange={(e) =>
              setEditForm({ ...editForm, description: e.target.value })
            }
          />
          <Toggle
            label="Is Active"
            checked={editForm.isActive}
            onChange={() =>
              setEditForm({ ...editForm, isActive: !editForm.isActive })
            }
          />
          <Button type="submit" className="w-full">
            Update Operator
          </Button>
        </form>
      </Modal>
    </div>
  );
}

export function AgentOperatorUsers() {
  const [users, setUsers] = useState([]);
  const [operators, setOperators] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [form, setForm] = useState({
    username: "",
    password: "",
    operatorId: "",
  });
  const [editForm, setEditForm] = useState({
    username: "",
    password: "",
    operatorId: "",
    isActive: true,
  });
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const fetchData = () => {
    api.get("/agent/operator-users").then((r) => setUsers(r.data.data));
    api.get("/agent/operators").then((r) => setOperators(r.data.data));
  };
  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/agent/operator-users", form);
      toast.success("User created!");
      setShowCreate(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || "Error.");
    }
  };

  const handleEditOpen = (user) => {
    setEditForm({
      username: user.username,
      password: "",
      operatorId: user.operator?.id || "",
      isActive: user.isActive,
    });
    setShowEdit(user);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/agent/operator-users/${showEdit.id}`, editForm);
      toast.success("User updated!");
      setShowEdit(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || "Error.");
    }
  };

  return (
    <div>
      <PageHeader
        title="Operators User List"
        action={<Button onClick={() => setShowCreate(true)}>Create</Button>}
      />
      <DataTable
        columns={[
          { header: "ID", key: "id" },
          { header: "Name", render: (r) => r.username || r.name },
          { header: "Operator", render: (r) => r.operator?.name || "-" },
          {
            header: "Is Active",
            render: (r) => (
              <StatusBadge status={r.isActive ? "Active" : "Inactive"} />
            ),
          },
        ]}
        data={users}
        total={users.length}
        page={1}
        actions={(r) => (
          <div className="flex gap-1">
            <button
              onClick={() => {
                navigator.clipboard.writeText(
                  `Username: ${r.username}\nPassword: ${r.plainPassword || "N/A"}\nLogin: ${window.location.origin}/login`,
                );
                toast.success("Credentials copied!");
              }}
              className="p-1.5 rounded-lg hover:bg-gray-100"
              title="Copy Credentials"
            >
              📋
            </button>
            <button
              onClick={async () => {
                try {
                  await impersonate(r.id);
                  navigate("/operator");
                } catch (e) {
                  toast.error("Failed.");
                }
              }}
              className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-500"
              title="Login as Operator"
            >
              🔑
            </button>
            <button
              onClick={() => handleEditOpen(r)}
              className="p-1.5 rounded-lg hover:bg-gray-100"
            >
              ✏️
            </button>
            <button
              onClick={async () => {
                await api.delete(`/agent/operator-users/${r.id}`);
                fetchData();
              }}
              className="p-1.5 rounded-lg hover:bg-red-50"
            >
              🗑️
            </button>
          </div>
        )}
      />

      {/* Create Modal */}
      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="Operator User Form"
      >
        <form onSubmit={handleCreate}>
          <FormInput
            label="User Name"
            required
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
          />
          <FormInput
            label="Password"
            required
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
          <FormSelect
            label="Select Operator"
            required
            placeholder="Select Option"
            options={operators.map((o) => ({ value: o.id, label: o.name }))}
            value={form.operatorId}
            onChange={(e) => setForm({ ...form, operatorId: e.target.value })}
          />
          <Button type="submit" className="w-full">
            Save User
          </Button>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={!!showEdit}
        onClose={() => setShowEdit(null)}
        title="Edit Operator User"
      >
        <form onSubmit={handleEdit}>
          <FormInput
            label="Username"
            required
            value={editForm.username}
            onChange={(e) =>
              setEditForm({ ...editForm, username: e.target.value })
            }
          />
          <FormInput
            label="New Password"
            type="password"
            value={editForm.password}
            onChange={(e) =>
              setEditForm({ ...editForm, password: e.target.value })
            }
            placeholder="Leave blank to keep current"
          />
          <FormSelect
            label="Select Operator"
            required
            placeholder="Select Option"
            options={operators.map((o) => ({ value: o.id, label: o.name }))}
            value={editForm.operatorId}
            onChange={(e) =>
              setEditForm({ ...editForm, operatorId: e.target.value })
            }
          />
          <Toggle
            label="Is Active"
            checked={editForm.isActive}
            onChange={() =>
              setEditForm({ ...editForm, isActive: !editForm.isActive })
            }
          />
          <Button type="submit" className="w-full">
            Update User
          </Button>
        </form>
      </Modal>
    </div>
  );
}

export function AgentTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showDetail, setShowDetail] = useState(null);

  useEffect(() => {
    api.get(`/agent/transactions?page=${page}&limit=10`).then((r) => {
      setTransactions(r.data.data);
      setTotal(r.data.total);
      setLoading(false);
    });
  }, [page]);

  const handleExport = async () => {
    const r = await api.get("/reports/export/transactions", {
      responseType: "blob",
    });
    const url = URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = "transactions.xlsx";
    a.click();
  };

  return (
    <div>
      <PageHeader title="Transactions List" />
      <DataTable
        columns={[
          { header: "ID", key: "id" },
          {
            header: "Amount",
            render: (r) => `₹${parseFloat(r.amount).toLocaleString()}`,
          },
          { header: "UTR Number", render: (r) => r.utrNumber || "-" },
          { header: "Notes", render: (r) => r.notes || "-" },
          {
            header: "Created",
            render: (r) => new Date(r.createdAt).toLocaleString(),
          },
          {
            header: "Cleared Date",
            render: (r) =>
              r.transactionClearTime
                ? new Date(r.transactionClearTime).toLocaleString()
                : "-",
          },
          {
            header: "Status",
            render: (r) => {
              const labels = {
                PENDING: "Pending",
                PICKED: "Picked",
                SUBMITTED: "Submitted",
                PAID: "Paid",
                CONFIRMED: "Confirmed",
                REJECTED: "Rejected",
              };
              const styles = {
                SUBMITTED: "bg-emerald-100 text-emerald-700",
                PICKED: "bg-blue-100 text-blue-700",
                REJECTED: "bg-red-100 text-red-700",
                PAID: "bg-purple-100 text-purple-700",
                CONFIRMED: "bg-teal-100 text-teal-700",
              };
              return (
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[r.status] || "bg-amber-100 text-amber-700"}`}
                >
                  {labels[r.status] || r.status}
                </span>
              );
            },
          },
        ]}
        data={transactions}
        total={total}
        page={page}
        onPageChange={setPage}
        loading={loading}
        onExport={handleExport}
        actions={(r) => (
          <button
            onClick={() => setShowDetail(r)}
            className="text-brand-500 text-sm"
          >
            View All
          </button>
        )}
      />

      <Modal
        open={!!showDetail}
        onClose={() => setShowDetail(null)}
        title="Transaction Details"
        maxWidth="max-w-md"
      >
        {showDetail && (
          <div className="text-sm space-y-2">
            {[
              ["Amount", `₹${parseFloat(showDetail.amount).toLocaleString()}`],
              ["Type", showDetail.transactionType],
              ["UTR", showDetail.utrNumber || "-"],
              ["Status", showDetail.status],
              [
                "Operator Pick",
                showDetail.operatorPickTime
                  ? new Date(showDetail.operatorPickTime).toLocaleString()
                  : "-",
              ],
              [
                "Clear Time",
                showDetail.transactionClearTime
                  ? new Date(showDetail.transactionClearTime).toLocaleString()
                  : "-",
              ],
            ].map(([k, v]) => (
              <div
                key={k}
                className="flex justify-between py-1.5 border-b border-gray-50"
              >
                <span className="text-gray-500">{k}</span>
                <span className="font-medium">{v}</span>
              </div>
            ))}
            {showDetail.merchant && (
              <div className="mt-3 pt-3 border-t">
                <p className="font-semibold text-gray-800 mb-2">Merchant</p>
                <div className="flex justify-between">
                  <span className="text-gray-500">Name</span>
                  <span>{showDetail.merchant.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Max Limit</span>
                  <span>
                    ₹
                    {parseFloat(
                      showDetail.merchant.maxPaymentLimit || 0,
                    ).toLocaleString()}
                  </span>
                </div>
              </div>
            )}
            {showDetail.operator && (
              <div className="mt-3 pt-3 border-t">
                <p className="font-semibold text-gray-800 mb-2">Operator</p>
                <div className="flex justify-between">
                  <span className="text-gray-500">Name</span>
                  <span>{showDetail.operator.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Max Txn</span>
                  <span>
                    ₹
                    {parseFloat(
                      showDetail.operator.maxTransactionAmount || 0,
                    ).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Min Txn</span>
                  <span>
                    ₹
                    {parseFloat(
                      showDetail.operator.minTransactionAmount || 0,
                    ).toLocaleString()}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ═══ AGENT LEDGER ═══
export function AgentLedger() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState("");
  const [rates, setRates] = useState({ aedTodayRate: 1, usdtTodayRate: 1 });
  const [settlements, setSettlements] = useState([]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (selectedDate) {
      params.startDate = selectedDate;
      params.endDate = selectedDate;
    }
    Promise.all([
      api.get("/agent/ledger", { params }),
      api.get("/config/current-rates"),
      api.get("/agent/settlements"),
    ])
      .then(([txRes, rateRes, settlRes]) => {
        setTransactions(txRes.data.data || []);
        const r = rateRes.data.data?.[0];
        if (r)
          setRates({
            aedTodayRate: parseFloat(r.aedTodayRate || 1),
            usdtTodayRate: parseFloat(r.usdtTodayRate || 1),
          });
        const daySettlements = (settlRes.data.data || []).filter((s) => {
          if (!selectedDate)
            return s.status === "CONFIRMED" || s.status === "SUBMITTED";
          // Dated by when the settlement was raised — updatedAt shifts on any
          // later edit, which would move it to the wrong day.
          const d = new Date(s.createdAt).toISOString().split("T")[0];
          return (
            d === selectedDate &&
            (s.status === "CONFIRMED" || s.status === "SUBMITTED")
          );
        });
        setSettlements(daySettlements);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [selectedDate]);

  // Each transaction is converted at the rate frozen on it, so re-opening an
  // old date always shows the same AED/USDT figures.
  const groupByOperator = () => {
    const groups = {};
    transactions.forEach((tx) => {
      const opName = tx.operator?.name || "No Operator";
      if (!groups[opName])
        groups[opName] = {
          operatorName: opName,
          totalINR: 0,
          totalCommission: 0,
          netAED: 0,
          netUSDT: 0,
          lastRate: 0,
        };
      const amt = parseFloat(tx.amount);
      const comm = parseFloat(tx.agentCommission || 0);
      const aed = parseFloat(tx.aedRate || 0);
      const usdt = parseFloat(tx.usdtRate || 0);
      groups[opName].totalINR += amt;
      groups[opName].totalCommission += comm;
      if (aed > 0) {
        groups[opName].netAED += (amt - comm) / aed;
        groups[opName].lastRate = aed;
      }
      if (usdt > 0) groups[opName].netUSDT += (amt - comm) / usdt;
    });
    return groups;
  };

  const operatorGroups = groupByOperator();
  const { aedTodayRate, usdtTodayRate } = rates;
  const grandINR = Object.values(operatorGroups).reduce(
    (s, g) => s + g.totalINR,
    0,
  );
  const grandCommission = Object.values(operatorGroups).reduce(
    (s, g) => s + g.totalCommission,
    0,
  );
  const grandBalance = grandINR - grandCommission;
  const grandBalanceAed = Object.values(operatorGroups).reduce(
    (s, g) => s + g.netAED,
    0,
  );
  const grandBalanceUsdt = Object.values(operatorGroups).reduce(
    (s, g) => s + g.netUSDT,
    0,
  );
  // Effective rate actually used across the shown entries
  const effectiveAedRate =
    grandBalanceAed > 0 ? grandBalance / grandBalanceAed : aedTodayRate;
  const totalSettledAed = settlements
    .filter((s) => s.currency === "AED")
    .reduce((s, r) => s + parseFloat(r.amount), 0);
  const totalSettledUsdt = settlements
    .filter((s) => s.currency === "USDT")
    .reduce((s, r) => s + parseFloat(r.amount), 0);
  // Settlements use the rate frozen on each one (server-supplied amountInr).
  const totalSettledInr = settlements.reduce(
    (s, r) =>
      s +
      (r.amountInr != null
        ? parseFloat(r.amountInr)
        : parseFloat(r.amount) *
          (r.currency === "USDT" ? usdtTodayRate : aedTodayRate)),
    0,
  );
  const netBalance = grandBalance - totalSettledInr;
  const netBalanceAed = grandBalanceAed - totalSettledAed;
  const netBalanceUsdt = grandBalanceUsdt - totalSettledUsdt;


  if (loading) return <div className="p-6 text-gray-400">Loading...</div>;

  const dateLabel = selectedDate
    ? new Date(selectedDate)
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
        })
        .replace(/\//g, "-")
    : "All";

  return (
    <div className="printable">
      <PrintHeader title="Agent Collection Ledger" subtitle={`Date: ${selectedDate || "All dates"}`} />
      <div className="no-print flex items-center justify-between mb-4">
        <PageHeader title="Agent Collection Ledger" />
        <div className="flex items-end gap-2">
          <PrintButton />
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Select Date
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="h-9 px-3 text-sm border border-gray-200 rounded-lg outline-none focus:border-brand-500 transition-mac"
            />
          </div>
          {selectedDate && (
            <button
              onClick={() => setSelectedDate("")}
              className="h-9 px-3 text-sm text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="bg-brand-500 text-white text-center py-3 rounded-t-xl font-bold text-lg">
        AGENT DAILY COLLECTION ({dateLabel})
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-b-xl">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-brand-50">
              <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700 w-12">
                SR.
              </th>
              <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">
                OPERATOR
              </th>
              <th className="border border-gray-300 px-3 py-2 text-right font-semibold text-gray-700">
                AED RATE
              </th>
              <th className="border border-gray-300 px-3 py-2 text-right font-semibold text-gray-700">
                TOTAL AMOUNT (INR)
              </th>
              <th className="border border-gray-300 px-3 py-2 text-right font-semibold text-gray-700">
                COMMISSION (INR)
              </th>
              <th className="border border-gray-300 px-3 py-2 text-right font-semibold text-gray-700">
                LENA BALANCE
              </th>
              <th className="border border-gray-300 px-3 py-2 text-right font-semibold text-gray-700">
                SETTLEMENT (AED)
              </th>
              <th className="border border-gray-300 px-3 py-2 text-right font-semibold text-gray-700">
                NET LENA
              </th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(operatorGroups).length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="border border-gray-200 px-3 py-6 text-center text-gray-400"
                >
                  No cleared transactions{selectedDate ? " for this date" : ""}.
                </td>
              </tr>
            ) : (
              Object.values(operatorGroups).map((group, idx) => {
                const balance = group.totalINR - group.totalCommission;
                return (
                  <tr
                    key={idx}
                    className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="border border-gray-200 px-3 py-2 text-center text-gray-500">
                      {idx + 1}
                    </td>
                    <td className="border border-gray-200 px-3 py-2 font-semibold text-gray-800">
                      {group.operatorName.toUpperCase()}
                    </td>
                    <td className="border border-gray-200 px-3 py-2 text-right text-gray-500 text-xs">
                      {group.lastRate > 0 ? group.lastRate.toFixed(2) : "-"}
                    </td>
                    <td className="border border-gray-200 px-3 py-2 text-right font-medium">
                      ₹{fmt(group.totalINR)}
                    </td>
                    <td className="border border-gray-200 px-3 py-2 text-right text-red-600 font-medium">
                      ₹{fmt(group.totalCommission)}
                    </td>
                    <td className="border border-gray-200 px-3 py-2 text-right">
                      <div className="font-semibold text-green-600">
                        ₹{fmt(balance)}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        AED {fmt(group.netAED)} | USDT {fmt(group.netUSDT)}
                      </div>
                    </td>
                    {idx === 0 && (
                      <>
                        <td
                          className="border border-gray-200 px-3 py-2 text-right text-orange-600 font-medium"
                          rowSpan={Object.keys(operatorGroups).length}
                        >
                          {totalSettledAed > 0 && (
                            <div>AED {fmt(totalSettledAed)}</div>
                          )}
                          {totalSettledUsdt > 0 && (
                            <div>USDT {fmt(totalSettledUsdt)}</div>
                          )}
                          <div className="text-xs text-gray-400 mt-0.5">
                            ₹{fmt(totalSettledInr)}
                          </div>
                        </td>
                        <td
                          className="border border-gray-200 px-3 py-2 text-right"
                          rowSpan={Object.keys(operatorGroups).length}
                        >
                          <div className="font-semibold text-blue-600">
                            ₹{fmt(netBalance)}
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">
                            AED {fmt(netBalanceAed)} | USDT{" "}
                            {fmt(netBalanceUsdt)}
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })
            )}
            <tr className="bg-brand-500 text-white font-bold">
              <td
                className="border border-gray-300 px-3 py-2 text-center"
                colSpan={2}
              >
                TOTAL
              </td>
              <td className="border border-gray-300 px-3 py-2 text-right">
                {effectiveAedRate > 0 ? effectiveAedRate.toFixed(2) : "-"}
              </td>
              <td className="border border-gray-300 px-3 py-2 text-right">
                ₹{fmt(grandINR)}
              </td>
              <td className="border border-gray-300 px-3 py-2 text-right">
                ₹{fmt(grandCommission)}
              </td>
              <td className="border border-gray-300 px-3 py-2 text-right">
                <div>₹{fmt(grandBalance)}</div>
                <div className="text-xs font-normal mt-0.5 opacity-90">
                  AED {fmt(grandBalanceAed)} | USDT{" "}
                  {fmt(grandBalanceUsdt)}
                </div>
              </td>
              <td className="border border-gray-300 px-3 py-2 text-right">
                {totalSettledAed > 0 && <div>AED {fmt(totalSettledAed)}</div>}
                {totalSettledUsdt > 0 && (
                  <div>USDT {fmt(totalSettledUsdt)}</div>
                )}
              </td>
              <td className="border border-gray-300 px-3 py-2 text-right">
                <div>₹{fmt(netBalance)}</div>
                <div className="text-xs font-normal mt-0.5 opacity-90">
                  AED {fmt(netBalanceAed)} | USDT{" "}
                  {fmt(netBalanceUsdt)}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap gap-4 text-sm">
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">AED Rate:</span>
          <span className="ml-2 font-bold text-gray-800">
            {effectiveAedRate > 0 ? effectiveAedRate.toFixed(2) : "-"}
          </span>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">Total INR:</span>
          <span className="ml-2 font-bold text-gray-800">₹{fmt(grandINR)}</span>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">Commission:</span>
          <span className="ml-2 font-bold text-red-700">
            ₹{fmt(grandCommission)}
          </span>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">Lena Balance:</span>
          <span className="ml-2 font-bold text-green-600">
            ₹{fmt(grandBalance)}
          </span>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">Settlement:</span>
          {totalSettledAed > 0 && (
            <span className="ml-2 font-bold text-orange-600">
              AED {fmt(totalSettledAed)}
            </span>
          )}
          {totalSettledUsdt > 0 && (
            <span className="ml-2 font-bold text-orange-600">
              USDT {fmt(totalSettledUsdt)}
            </span>
          )}
          {totalSettledAed === 0 && totalSettledUsdt === 0 && (
            <span className="ml-2 font-bold text-gray-400">—</span>
          )}
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">Net Lena:</span>
          <span className="ml-2 font-bold text-blue-700">
            ₹{fmt(netBalance)}
          </span>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">Net AED:</span>
          <span className="ml-2 font-bold text-green-700">
            {fmt(netBalanceAed)}
          </span>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">Net USDT:</span>
          <span className="ml-2 font-bold text-purple-700">
            {fmt(netBalanceUsdt)}
          </span>
        </div>
      </div>
    </div>
  );
}
// ═══ AGENT SETTLEMENTS (with USDT wallet) ═══
export function AgentSettlements() {
  const [items, setItems] = useState([]);
  const [showSubmit, setShowSubmit] = useState(null);
  const [walletAddress, setWalletAddress] = useState("");
  const [walletRemark, setWalletRemark] = useState("");
  const [qrImage, setQrImage] = useState(null);
  const [savedProfile, setSavedProfile] = useState(null);
  const [showConfirm, setShowConfirm] = useState(null);
  const fetchData = () =>
    api.get("/agent/settlements").then((r) => setItems(r.data.data));

  useEffect(() => {
    fetchData();
    api
      .get("/agent/wallet-profile")
      .then((r) => setSavedProfile(r.data.data))
      .catch(() => {});
  }, []);

  const openSubmit = (r) => {
    setShowSubmit(r);
    setWalletAddress(savedProfile?.walletAddress || "");
    setWalletRemark(savedProfile?.walletRemark || "");

    setQrImage(null);
  };

  const handlePick = async (id) => {
    try {
      await api.post(`/agent/settlements/${id}/pick`);
      toast.success("Picked!");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || "Error.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append("walletAddress", walletAddress);
      formData.append("remark", walletRemark);
      if (qrImage) {
        formData.append("qrImage", qrImage);
      } else if (savedProfile?.walletQrImage) {
        formData.append("existingQrImage", savedProfile.walletQrImage);
      }
      await api.post(`/agent/settlements/${showSubmit.id}/submit`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Submitted!");
      setShowSubmit(null);
      // Refresh saved profile with latest
      api
        .get("/agent/wallet-profile")
        .then((r) => setSavedProfile(r.data.data))
        .catch(() => {});
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || "Error.");
    }
  };

  const handleConfirm = async (id) => {
    try {
      await api.post(`/agent/settlements/${id}/confirm`);
      toast.success("Confirmed! Payment received.");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || "Error.");
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt("Enter reject reason:");
    if (!reason) return;
    try {
      await api.post(`/agent/settlements/${id}/reject`, { reason });
      toast.success("Rejected.");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || "Error.");
    }
  };

  return (
    <div>
      <PageHeader title="Settlements" />

      {/* Saved Profile Banner */}
      {savedProfile?.walletAddress && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-4">
          {savedProfile.proofImage && (
            <img
              src={`/uploads/settlements/${savedProfile.walletQrImage}`}
              alt="QR"
              className="h-14 w-14 object-contain rounded-lg border border-green-200 bg-white"
            />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-green-700 mb-0.5">
              Saved Settlement Details
            </p>
            <p className="text-sm text-gray-700 font-mono truncate">
              {savedProfile.walletAddress}
            </p>
            {savedProfile.remark && (
              <p className="text-xs text-gray-500 mt-0.5">
                {savedProfile.remark}
              </p>
            )}
          </div>
          <p className="text-xs text-green-600 whitespace-nowrap">
            Auto-fills on submit
          </p>
        </div>
      )}

      <DataTable
        columns={[
          {
            header: "Amount",
            render: (r) =>
              `${r.currency || ""} ${parseFloat(r.amount).toLocaleString()}`,
          },
          { header: "Collector", render: (r) => r.collector?.name || "-" },
          { header: "Remark", render: (r) => r.remark || "-" },
          {
            header: "Wallet",
            render: (r) =>
              r.walletAddress ? (
                <span className="font-mono text-xs truncate max-w-[120px] block">
                  {r.walletAddress}
                </span>
              ) : (
                "-"
              ),
          },
          {
            header: "QR",
            render: (r) =>
              r.proofImage ? (
                <a
                  href={`/uploads/settlements/${r.proofImage}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-500 text-xs underline"
                >
                  View
                </a>
              ) : (
                "-"
              ),
          },
          {
            header: "Status",
            render: (r) => {
              const styles = {
                SUBMITTED: "bg-emerald-100 text-emerald-700",
                PICKED: "bg-blue-100 text-blue-700",
                REJECTED: "bg-red-100 text-red-700",
                PAID: "bg-purple-100 text-purple-700",
                CONFIRMED: "bg-teal-100 text-teal-700",
              };
              return (
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[r.status] || "bg-amber-100 text-amber-700"}`}
                >
                  {r.status}
                </span>
              );
            },
          },
          {
            header: "Date",
            render: (r) => new Date(r.createdAt).toLocaleString(),
          },
        ]}
        data={items}
        total={items.length}
        page={1}
        actions={(r) => (
          <div className="flex gap-1">
            {r.status === "PENDING" && (
              <Button
                onClick={() => handlePick(r.id)}
                variant="primary"
                className="h-7 px-2 text-xs"
              >
                Pick
              </Button>
            )}
            {r.status === "PICKED" && (
              <>
                <Button
                  onClick={() => openSubmit(r)}
                  variant="primary"
                  className="h-7 px-2 text-xs"
                >
                  Submit
                </Button>
                <Button
                  onClick={() => handleReject(r.id)}
                  variant="danger"
                  className="h-7 px-2 text-xs"
                >
                  Reject
                </Button>
              </>
            )}
            {r.status === "PAID" && (
  <Button
    onClick={() => setShowConfirm(r)}
    variant="primary"
    className="h-7 px-2 text-xs bg-teal-600 hover:bg-teal-700"
  >
    Confirm Received
  </Button>
)}
          </div>
        )}
      />

      {/* Submit Modal */}
      <Modal
        open={!!showSubmit}
        onClose={() => setShowSubmit(null)}
        title="Submit Settlement"
      >
        <form onSubmit={handleSubmit}>
          <p className="text-sm text-gray-500 mb-4">
            {showSubmit?.currency}{" "}
            {showSubmit && parseFloat(showSubmit.amount).toLocaleString()} —{" "}
            {showSubmit?.collector?.name || "Collector"}
          </p>

          {savedProfile?.walletQrImage && !qrImage && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-xl flex items-center gap-3">
              <img
                src={`/uploads/settlements/${savedProfile.walletQrImage}`}
                alt="QR"
                className="h-12 w-12 object-contain rounded border border-blue-200 bg-white"
              />
              <div>
                <p className="text-xs font-semibold text-blue-700">
                  Saved QR will be used
                </p>
                <p className="text-xs text-gray-400">
                  Upload new below to override for this submission
                </p>
              </div>
            </div>
          )}

          <FormInput
            label="USDT Wallet Address"
            required
            value={walletAddress}
            onChange={(e) => setWalletAddress(e.target.value)}
            placeholder="Enter wallet address"
          />
          <FormInput
            label="Remark"
            value={walletRemark}
            onChange={(e) => setWalletRemark(e.target.value)}
            placeholder="Optional remark"
          />
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              QR Image{" "}
              <span className="text-xs text-gray-400 font-normal">
                {savedProfile?.walletQrImage ? "(saved QR used if empty)" : ""}
              </span>
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setQrImage(e.target.files[0])}
              className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-brand-50 file:text-brand-600 hover:file:bg-brand-100"
            />
          </div>
          <Button type="submit" className="w-full">
            Submit Settlement
          </Button>
        </form>
      </Modal>
      {/* Confirm Modal — shows collector payment proof */}
<Modal open={!!showConfirm} onClose={() => setShowConfirm(null)} title="Confirm Payment Received">
  {showConfirm && (
    <div>
      {/* Payment details */}
      <div className="mb-4 p-4 bg-gray-50 border border-gray-100 rounded-xl space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-400">Amount</span>
          <span className="font-bold text-gray-800">{showConfirm.currency} {parseFloat(showConfirm.amount).toLocaleString()}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-400">Collector</span>
          <span className="font-medium">{showConfirm.collector?.name || "-"}</span>
        </div>
        {showConfirm.payRemark && (
          <div className="flex justify-between">
            <span className="text-gray-400">Collector Remark</span>
            <span className="font-medium">{showConfirm.payRemark}</span>
          </div>
        )}
      </div>

      {/* Payment screenshot */}
      {showConfirm.payScreenshot ? (
        <div className="mb-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Payment Screenshot</p>
          <a href={`/uploads/settlements/${showConfirm.payScreenshot}`} target="_blank" rel="noreferrer">
            <img
              src={`/uploads/settlements/${showConfirm.payScreenshot}`}
              alt="Payment Screenshot"
              className="w-full rounded-xl border border-gray-200 object-contain max-h-64 bg-gray-50 cursor-pointer hover:opacity-90"
            />
            <p className="text-xs text-blue-500 text-center mt-1">Click to open full size</p>
          </a>
        </div>
      ) : (
        <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
          No payment screenshot uploaded by collector.
        </div>
      )}

      <div className="flex gap-2">
        <Button
          onClick={() => setShowConfirm(null)}
          variant="secondary"
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          onClick={async () => {
            await handleConfirm(showConfirm.id);
            setShowConfirm(null);
          }}
          className="flex-1 bg-teal-600 hover:bg-teal-700"
        >
          ✓ Confirm Received
        </Button>
      </div>
    </div>
  )}
</Modal>
    </div>
  );
}

export function AgentSettlementDetails() {
  const [profile, setProfile] = useState({ walletAddress: "", remark: "" });
  const [qrImage, setQrImage] = useState(null);
  const [existing, setExisting] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/agent/wallet-profile")
      .then((r) => {
        setExisting(r.data.data);
        setProfile({
          walletAddress: r.data.data?.walletAddress || "",
          remark: r.data.data?.remark || "",
        });
      })
      .catch(() => {});
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!profile.walletAddress) {
      toast.error("Wallet address required.");
      return;
    }
    setSaving(true);
    try {
      // Save by creating a dummy settlement with wallet details
      // Instead we just store via a direct settlement update approach
      // We'll POST to a dedicated save endpoint
      const fd = new FormData();
      fd.append("walletAddress", profile.walletAddress);
      fd.append("remark", profile.remark);
      if (qrImage) fd.append("qrImage", qrImage);
      await api.post("/agent/settlement-details", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Settlement details saved!");
      api.get("/agent/wallet-profile").then((r) => setExisting(r.data.data));
      setQrImage(null);
    } catch (e) {
      toast.error("Error saving.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title="Settlement Details" />
      <div className="max-w-lg">
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-6 text-sm text-blue-700">
          Save your wallet address and QR once — it will auto-fill every time
          you submit a settlement.
        </div>

        {existing?.walletAddress && (
          <div className="bg-white rounded-2xl shadow-card p-4 mb-6 border border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
              Current Saved Details
            </p>
            <div className="flex gap-4 items-start">
              {existing.walletQrImage && (
                <img
                  src={`/uploads/settlements/${existing.walletQrImage}`}
                  alt="QR"
                  className="h-24 w-24 object-contain rounded-xl border border-gray-200 bg-gray-50"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400 mb-1">Wallet Address</p>
                <p className="text-sm font-mono text-gray-800 break-all">
                  {existing.walletAddress}
                </p>
                {existing.remark && (
                  <>
                    <p className="text-xs text-gray-400 mt-2 mb-1">Remark</p>
                    <p className="text-sm text-gray-600">{existing.remark}</p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-card p-6">
          <p className="text-sm font-semibold text-gray-700 mb-4">
            {existing?.walletAddress ? "Update Details" : "Add Details"}
          </p>
          <form onSubmit={handleSave}>
            <FormInput
              label="USDT Wallet Address"
              required
              value={profile.walletAddress}
              onChange={(e) =>
                setProfile({ ...profile, walletAddress: e.target.value })
              }
              placeholder="Enter your USDT wallet address"
            />
            <FormInput
              label="Remark"
              value={profile.remark}
              onChange={(e) =>
                setProfile({ ...profile, remark: e.target.value })
              }
              placeholder="Optional remark"
            />
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                QR Image{" "}
                {existing?.proofImage && (
                  <span className="text-xs text-gray-400 font-normal">
                    (upload new to replace)
                  </span>
                )}
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setQrImage(e.target.files[0])}
                className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-brand-50 file:text-brand-600 hover:file:bg-brand-100"
              />
              {qrImage && (
                <p className="text-xs text-gray-400 mt-1">
                  Selected: {qrImage.name}
                </p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={saving}>
              {saving ? "Saving..." : "Save Details"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
